export enum FilterOperator {
  Equals = 0,
  NotEquals = 1,
  GreaterThan = 2,
  LessThan = 3,
  GreaterThanOrEqual = 4,
  LessThanOrEqual = 5,
  Like = 6,
  In = 7,
  IsDistinctFrom = 8,
  InOrNull = 9,
}

export interface DownloadFilterCriteria {
  column: string;
  operator: FilterOperator;
  value: string;
}

export interface DownloadRequest {
  columns: string[];
  filters: DownloadFilterCriteria[];
}

export function toEqualsFilter(column: string, value: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.Equals, value };
}

export function toInFilter(column: string, values: string[]): DownloadFilterCriteria {
  return { column, operator: FilterOperator.In, value: values.join(',') };
}

export function toFilter(column: string, values: string[]): DownloadFilterCriteria {
  return values.length === 1 ? toEqualsFilter(column, values[0]) : toInFilter(column, values);
}

// ─── Date filter helpers ───────────────────────────────────────────────────

export function toDateEqualsFilter(column: string, date: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.Equals, value: date };
}

export function toDateOnOrBeforeFilter(column: string, date: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.LessThanOrEqual, value: date };
}

export function toDateOnOrAfterFilter(column: string, date: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.GreaterThanOrEqual, value: date };
}

export function toDateBeforeFilter(column: string, date: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.LessThan, value: date };
}

export function toDateAfterFilter(column: string, date: string): DownloadFilterCriteria {
  return { column, operator: FilterOperator.GreaterThan, value: date };
}

export function toDateBetweenFilters(column: string, from: string, to: string): DownloadFilterCriteria[] {
  return [
    { column, operator: FilterOperator.GreaterThanOrEqual, value: from },
    { column, operator: FilterOperator.LessThanOrEqual, value: to },
  ];
}

export type ExpiryDateType = 'EQUALS' | 'ON_OR_BEFORE' | 'ON_OR_AFTER' | 'BETWEEN' | 'BEFORE' | 'AFTER';

/**
 * Builds one or two DownloadFilterCriteria for the expiry date filter based
 * on the selected date type from the material-rate advanced filter.
 *
 * For BETWEEN, pass both `date` (from) and `dateTo` (to).
 * For all other types, only `date` is required.
 */
export function toExpiryDateFilters(column: string, type: ExpiryDateType, date: string, dateTo?: string): DownloadFilterCriteria[] {
  switch (type) {
    case 'EQUALS':
      return [toDateEqualsFilter(column, date)];
    case 'ON_OR_BEFORE':
      return [toDateOnOrBeforeFilter(column, date)];
    case 'ON_OR_AFTER':
      return [toDateOnOrAfterFilter(column, date)];
    case 'BEFORE':
      return [toDateBeforeFilter(column, date)];
    case 'AFTER':
      return [toDateAfterFilter(column, date)];
    case 'BETWEEN':
      return toDateBetweenFilters(column, date, dateTo ?? date);
  }
}
